import { forwardRef, type ButtonHTMLAttributes } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'destructive' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className = '', variant = 'primary', size = 'md', children, ...props }, ref) => {
    const baseClasses = `
      inline-flex items-center justify-center flex-shrink-0
      font-medium rounded-[6px] whitespace-nowrap
      transition-[background,box-shadow] duration-150 ease-out
      focus:outline-none focus:ring-[3px] focus:ring-action-focus
      disabled:opacity-50 disabled:cursor-not-allowed
    `

    const variantClasses = {
      primary: 'bg-action-primary text-white border-none hover:bg-action-primary-hover',
      secondary: 'bg-transparent text-text-primary border border-border hover:bg-[#F5F5F3]',
      destructive: 'bg-transparent text-error border border-error hover:bg-error hover:text-white',
      ghost: 'bg-transparent text-text-secondary border-none hover:bg-[#F5F5F3] hover:text-text-primary',
    }

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-xs',
      md: 'px-5 py-2.5 text-sm',
      lg: 'px-6 py-3 text-base',
    }

    return (
      <button
        ref={ref}
        className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
        {...props}
      >
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button
